import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLocale } from "../../i18n/LocaleContext";
import { getSupabaseBrowser } from "@universe/supabase/browser";
import styles from "./AuthButton.module.scss";

import LoginIcon from "../../assets/images/svg/icon-login.svg?react";

interface AuthButtonProps {
  collapse?: boolean;
  suppressHover?: boolean;
}

export function AuthButton({ collapse = false, suppressHover = false }: AuthButtonProps) {
  const { user, isAuthenticated, openAuthModal } = useAuth();
  const { t, localePath } = useLocale();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (suppressHover) {
      setIsMouseOver(false);
      setIsMouseDown(false);
    }
  }, [suppressHover]);

  const handleClick = () => {
    if (isAuthenticated) {
      setShowDropdown((prev) => !prev);
    } else {
      openAuthModal();
    }
  };

  const handleSignOut = async () => {
    const supabase = getSupabaseBrowser();
    await supabase.auth.signOut();
    setShowDropdown(false);
  };

  const classes = [styles.UIButton, styles.UserBtn];
  if (collapse) classes.push(styles.Collapse);
  if (isMouseOver && !suppressHover) classes.push(styles.MouseOver);
  if (isMouseDown && !suppressHover) classes.push(styles.MouseDown);

  const buttonText = isAuthenticated ? t("nav.myAccount") : t("auth.signIn");

  // Get user avatar from Supabase user metadata
  const userImage = user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture;

  return (
    <div className={classes.join(" ")}>
      <div
        className={styles.ButtonBG}
        onClick={handleClick}
        onMouseEnter={() => {
          if (!suppressHover) setIsMouseOver(true);
        }}
        onMouseLeave={() => {
          if (!suppressHover) {
            setIsMouseOver(false);
            setIsMouseDown(false);
          }
        }}
        onMouseDown={() => {
          if (!suppressHover) setIsMouseDown(true);
        }}
        onMouseUp={() => {
          if (!suppressHover) {
            setIsMouseOver(false);
            setIsMouseDown(false);
          }
        }}
      >
        <div className={styles.ButtonIcon}>
          {isAuthenticated && userImage ? (
            <img
              src={userImage}
              alt={t("auth.profileAlt")}
              className={styles.ProfileImage}
            />
          ) : (
            <LoginIcon className={styles.IconImage} />
          )}
        </div>
        {!collapse && <span className={styles.BtnText}>{buttonText}</span>}
      </div>
      {showDropdown && isAuthenticated && (
        <div className={styles.Dropdown}>
          <Link
            to={localePath("/account")}
            className={styles.DropdownItem}
            onClick={() => setShowDropdown(false)}
          >
            {t("nav.myAccount")}
          </Link>
          <button onClick={handleSignOut} className={styles.DropdownItem}>
            {t("account.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}

export default AuthButton;
